import { Logger } from '@/utils/logger';
import { useControlledDialogProps } from '@montugroup/circuit-shared';
import { getTsCustomerPortalClient } from '@montugroup/circuit-shared/api/customer-portal.client';
import { ErrorMessage } from '@montugroup/circuit-shared/components/common/ErrorMessage';
import AuthService from '@montugroup/circuit-shared/services/authentication.service';
import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LoadingButton,
  Radio,
  RadioGroup,
  Stack,
  toast,
  Typography,
} from '@montugroup/design-system';
import { FormControlLabel } from '@mui/material';
import type { SubmitHandler } from 'react-hook-form';
import { Controller, useForm } from 'react-hook-form';

interface DeactivateAccountForm {
  shouldDeleteData: 'yes' | 'no' | null;
}

const logger = new Logger('DeactivateAccountModal');
const client = getTsCustomerPortalClient();

const successMessage = 'Successfully deactivated account';
const errorMessage = 'Failed to deactivate account';

export function DeactivateAccountModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { isPending, mutateAsync, reset, error, contractEndpoint } = client.deactivateUserAccount.useMutation();

  const {
    control,
    handleSubmit,
    reset: formReset,
    formState: { isDirty },
  } = useForm<DeactivateAccountForm>({
    defaultValues: {
      shouldDeleteData: null,
    },
  });

  const deactivatAccount: SubmitHandler<DeactivateAccountForm> = async (formData: DeactivateAccountForm) => {
    try {
      await mutateAsync({
        body: { data: { shouldDeleteData: formData.shouldDeleteData === 'yes' } },
      });

      toast.success(successMessage);
      logger.info(successMessage, { shouldDeleteData: formData.shouldDeleteData });
      // TODO: Add ability to direct to custom 'account deactivated URL' on logout for this use case
      AuthService.logout({ reason: 'deactivation' });
    } catch (e) {
      logger.error(errorMessage, e);
    }
  };

  const controlledDialogProps = useControlledDialogProps({
    open,
    onClose: () => {
      onClose();
      formReset();
      reset();
    },
  });

  return (
    <Dialog {...controlledDialogProps} fullWidth>
      <DialogTitle variant="subtitle1" fontWeight={'600'}>
        Deactivate account
      </DialogTitle>
      <DialogContent>
        <Alert severity="warning">
          By deactivating your account, you will no longer be able to log into uMeds or place orders.
        </Alert>
        <Stack sx={{ marginTop: 6, marginBottom: 1 }} gap={1} direction={'column'}>
          <Typography variant="body1">Would you like to delete your user profile and eScript tokens?</Typography>
          <Typography variant="caption" color="textSecondary">
            Only your user profile details and unused eScript tokens will be deleted. Your order history will not be
            deleted.
          </Typography>
        </Stack>
        <Controller
          name="shouldDeleteData"
          control={control}
          rules={{ required: true }}
          render={({ field: { ref: _ref, ...field } }) => (
            <RadioGroup {...field}>
              <Stack direction={'row'} gap={1}>
                <FormControlLabel value="yes" control={<Radio />} label="Yes" data-testid="shouldDeleteData-yes" />
                <FormControlLabel value="no" control={<Radio />} label="No" data-testid="shouldDeleteData-no" />
              </Stack>
            </RadioGroup>
          )}
        />
      </DialogContent>
      <ErrorMessage error={error} contractEndpoint={contractEndpoint} />
      <DialogActions>
        <LoadingButton
          variant="text"
          loading={isPending}
          color="primary"
          type="submit"
          onClick={controlledDialogProps.onClose}
        >
          Cancel
        </LoadingButton>
        <LoadingButton
          variant="contained"
          color="error"
          loading={isPending}
          disabled={!isDirty}
          type="submit"
          onClick={handleSubmit(deactivatAccount)}
        >
          Deactivate account
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
